import React, { useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "./FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { SingleTextForm, InputMatches } from "./SingleTextForm";
import { OUTCOMES } from "../../../constants";
import { Button } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import { default as NumberFormat } from "react-number-format";
import { estateApi } from "../../../apis";
import { AssetRealEstate } from "../../../models/AssetDetail";
import { Outcomes } from "./PropertyOwnershipScreen";
import { AssetContext } from "../../../AssetContextWrapper";
interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const NumberFormatCustom = React.forwardRef<NumberFormat<any>, CustomProps>(
  function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;

    return (
      <NumberFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator
        decimalSeparator="."
        allowNegative={false}
        decimalScale={0}
        fixedDecimalScale={false}
        isNumericString={true}
      />
    );
  }
);

export const PropertyValueScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, user } = useAuthContext()
  const { assetsLoaded } = useContext(AssetContext);
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const primaryRealEstate = activeEstate?.primaryRealEstate;

  // Only allow for positive (including 0) numbers.
  const inputMatches: InputMatches = {
    regexp: new RegExp("^[0-9]{1,3}(?:,?[0-9]{3})*$"),
    failMessage:
      "Fair Market Value must be a positive non-decimal currency value.",
  };

  useEffect(() => {
    if (assetsLoaded && !activeEstate?.primaryRealEstate) {
      currentScreen &&
        flow?.navigateToNext(currentScreen, Outcomes.skip, navigate, {
          replace: true,
        });
    }
  }, [assetsLoaded]);

  const handleComplete = async (propertyValue: string): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    // Database does not store commas. Change to empty string.
    propertyValue = propertyValue.replace(/,/g, "");

    if (activeEstate?.id && user?.id && activeEstate?.primaryRealEstate) {
      activeEstate.primaryRealEstate.value =
        parseInt(propertyValue, 10) || null;
      try {
        await estateApi.saveAsset(primaryRealEstate,activeEstate.id, user.id);
      } catch (error) {
        throw new Error("could not save data");
      }
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  const fullAddress = (
    primaryRealEstate?.detail as AssetRealEstate
  )?.getFullAddress();

  return currentScreen ? (
    <FlowScreenWrapper
      title="What was the fair market value (FMV) of the house?"
      subTitle="We need this to help determine whether or not the estate exceeds the maximum value allowed for a shortened or simplified probate process."
    >
      <>
        {fullAddress && (
          <Button
            endIcon={<LinkIcon />}
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => {
              window.open(`https://www.zillow.com/homes/${fullAddress}`);
            }}
          >
            Check it on Zillow
          </Button>
        )}
        <SingleTextForm
          initText={primaryRealEstate?.value?.toString()}
          inputMatches={inputMatches}
          errorText="Please enter estimated fair market value or let us know if you're not sure"
          textLabel="Estimated fair market value"
          onComplete={handleComplete}
          onBack={() => navigate(-1)}
          inputComponent={NumberFormatCustom}
          prefix="$"
          noneTextOption="I’m not sure"
        />
      </>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
